/**
 * Navigation Drawer Component
 * src/components/Navigation.jsx
 */

import {
  Home, Building2, TrendingUp, Receipt, Coins, Plane, Target,
  BookOpen, BarChart2, CreditCard, History, X, LogOut, Settings, AlertCircle, FileText,
} from 'lucide-react';

const SECTIONS = [
  {
    label: 'INICIO',
    items: [
      { key: 'dashboard',  label: 'Dashboard',  icon: Home       },
      { key: 'pagos',      label: 'Pagos',      icon: CreditCard },
    ],
  },
  {
    label: 'FINANZAS',
    items: [
      { key: 'patrimonio', label: 'Patrimonio', icon: Building2   },
      { key: 'pasivos',    label: 'Pasivos',    icon: AlertCircle },
      { key: 'ingresos',   label: 'Ingresos',   icon: TrendingUp  },
      { key: 'gastos',     label: 'Gastos',     icon: Receipt     },
      { key: 'proyeccion', label: 'Proyección', icon: BarChart2   },
    ],
  },
  {
    label: 'INVERSIONES',
    items: [
      { key: 'fondos',     label: 'Fondos',     icon: Coins      },
      { key: 'vacaciones', label: 'Vacaciones', icon: Plane      },
      { key: 'metas',      label: 'Metas',      icon: Target     },
    ],
  },
  {
    label: 'SISTEMA',
    items: [
      { key: 'amex',       label: 'AMEX Estados',   icon: FileText  },
      { key: 'catalogos',  label: 'Catálogos',      icon: BookOpen  },
      { key: 'historial',  label: 'Historial',      icon: History   },
      { key: 'settings',   label: 'Configuración',  icon: Settings  },
    ],
  },
];

export default function Navigation({ open, onClose, currentPage, onNavigate, user, userRole, userPermissions }) {
  const displayName = user?.displayName || user?.email || '';
  const initials    = displayName.charAt(0).toUpperCase() || '?';
  const email       = user?.email || '';
  const isAdmin     = userRole === 'Admin';

  // Returns true if this user can see the given module key
  const canSee = (key) => {
    if (isAdmin || !userPermissions) return true;
    const perm = userPermissions[key];
    // Support legacy { read, write } format (treated as access=true) and boolean
    if (typeof perm === 'object' && perm !== null) return true;
    return perm !== false;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`drawer-overlay${open ? ' drawer-overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside className={`drawer${open ? ' drawer--open' : ''}`} aria-label="Navegación principal">

        {/* Header */}
        <div className="drawer-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
              {initials}
            </div>
            <span className="drawer-logo">Takiro Finances</span>
          </div>
          <button className="btn-close-drawer" onClick={onClose} aria-label="Cerrar menú">
            <X size={20} />
          </button>
        </div>

        {/* Menu sections */}
        <nav className="drawer-nav">
          {SECTIONS.map((section, si) => {
            const visibleItems = section.items.filter(({ key }) => canSee(key));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.label}>
                {si > 0 && (
                  <div style={{ height: 1, background: 'var(--border-color)', margin: '0.4rem 0.75rem' }} />
                )}
                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-light)', padding: '0.5rem 0.75rem 0.2rem', textTransform: 'uppercase' }}>
                  {section.label}
                </div>
                {visibleItems.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    className={`drawer-item${currentPage === key ? ' active' : ''}`}
                    onClick={() => onNavigate(key)}
                  >
                    <Icon size={18} className="drawer-item-icon" />
                    <span className="drawer-item-label">{label}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div style={{ borderTop: '1px solid var(--border-color)', padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1a472a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
            <div style={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 700, marginTop: '0.1rem' }}>{userRole || 'Admin'}</div>
          </div>
          <LogOut size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
        </div>

      </aside>
    </>
  );
}
